import { calgaryCalendarDate } from '../../utils/calgaryTime';
import { useState, useMemo, useCallback, useEffect } from "react";
import "./Billing.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faArrowUpRightFromSquare, faArrowUp, faClock, faList, faCalendarWeek, faFileInvoice, faPenToSquare, faTrash, faChartLine } from "@fortawesome/free-solid-svg-icons";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { isFeatureEnabled } from "@frontend/AppSettings/environmentConfig";
import FinancialService from "../../AppSettings/services/FinancialService";
import InvoiceService from "../../AppSettings/services/InvoiceService";
import MatterService from "../../AppSettings/services/MatterService";
import { buildInvoiceNumberMap, resolveInvoiceNumber } from "../../helpers/invoiceNumber";
import { normalizeToArray } from "../../helpers/responseHelpers";
import { parseLocalDate, formatLocalDate } from "../../helpers/dateHelpers";
import Table from "../../Components/Table/Table";
import type { Column } from "@frontend/Models/Table/TableModels";
import LogBillableEntryModal from "./Components/LogBillableEntryModal/LogBillableEntryModal";
import BillingExportModal from "./Components/BillingExportModal/BillingExportModal";
import AlreadyInvoicedPanel from "./Components/AlreadyInvoicedPanel/AlreadyInvoicedPanel";
import BillingChartsModal from "./Components/BillingChartsModal/BillingChartsModal";
import GeneralTimesheetView from "../GeneralTimesheetView/GeneralTimesheetView";
import useCurrentEmployee from "@frontend/AppSettings/hooks/useCurrentEmployee";
import useActiveEmployeeId from "@frontend/AppSettings/hooks/useActiveEmployeeId";

interface BillingItem {
  type: string;
  id: string;
  rawId: string;
  bt_id?: string; // billed-time primary key; set for Billable Time rows, blank otherwise
  amount: number;
  description: string;
  date: string;
  dateObj: Date;
  employee: string;
  notes?: string;
  projectNumber: string;
  projectStatus: string;
  costCode: string;
  hoursQuantity: number;
  costRate: number;
  billRate: number;
  invoiceNumber: string;
  rawDateValue?: string;
  employeeId?: string;
  isBillable?: boolean; // A3: billable-time only; undefined for fixed-fee/other rows (treated as billable)
}

interface BillingMetrics {
  uninvoicedAmount: number;
  uninvoicedCount: number;
  totalBilling: number;
  topCategory: string;
  invoicedAmount: number;
}

/**
 * Billing component - Dashboard view for managing all billing types
 * Displays summary metrics, filters, and a unified billing table
 */
const Billing: React.FC = () => {
  const currentEmployee = useCurrentEmployee();
  const activeEmployeeId = useActiveEmployeeId();
  const permissionsResolved = currentEmployee !== null;
  const permishId = currentEmployee?.permish_id ?? null;
  const rawPermishIdNum = typeof permishId === 'string' ? parseInt(permishId, 10) : permishId;
  // When no permission is attached, default to the most restrictive view (level 3).
  const permishIdNum =
    rawPermishIdNum === null || rawPermishIdNum === undefined || Number.isNaN(rawPermishIdNum)
      ? 3
      : rawPermishIdNum;
  const isTimesheetOnlyUser = permishIdNum === 2 || permishIdNum === 3;

  const [typeFilter, setTypeFilter] = useState<string>("All types");
  const [dateFilter, setDateFilter] = useState<string>("All time");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [showInvoicedPanel, setShowInvoicedPanel] = useState<boolean>(false);
  const [showChartsModal, setShowChartsModal] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<"list" | "timesheet">(isTimesheetOnlyUser ? "timesheet" : "list");
  const [invoiceFilter, setInvoiceFilter] = useState<"all" | "invoiced" | "uninvoiced">("all");
  const [editingItem, setEditingItem] = useState<BillingItem | null>(null);
  const [deleteItem, setDeleteItem] = useState<BillingItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editFormData, setEditFormData] = useState<{
    amount: string; description: string; date: string; hours: string;
  }>({ amount: "", description: "", date: "", hours: "" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // A5: viewMode initializes from the restrictive default (permish 3) while useCurrentEmployee is still
  // loading, so an admin would land on the timesheet grid and never flip back. Once the employee record
  // RESOLVES, set the view from the LOADED permish in both directions (admin → list; permish 2/3 →
  // timesheet). This runs once on resolution and doesn't fight a later manual toggle (deps stable).
  //
  // Timesheet day locks: an admin opening a request email's deep link
  // (`?view=timesheet&requests=open&request=<id>`) lands on the timesheet, where GeneralTimesheetView
  // opens the Requests panel. Read once on resolution, like the rest of this effect.
  const location = useLocation();
  const wantsTimesheetView =
    isFeatureEnabled("timesheetDayLocks") && new URLSearchParams(location.search).get("view") === "timesheet";
  useEffect(() => {
    if (!permissionsResolved) return;
    setViewMode(isTimesheetOnlyUser || wantsTimesheetView ? "timesheet" : "list");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once on resolution (A5); a later manual toggle wins
  }, [permissionsResolved, isTimesheetOnlyUser]);

  const parseTimeRecordedToHours = useCallback((timeRecorded: string | undefined): number => {
    if (timeRecorded == null || String(timeRecorded).trim() === "") return 0;
    const s = String(timeRecorded).trim();
    if (s.includes(":")) {
      const parts = s.split(":");
      
      const h = parseInt(parts[0], 10) || 0;
      const m = parseInt(parts[1], 10) || 0;
      const sec = parseInt(parts[2], 10) || 0;
      return h + m / 60 + sec / 3600;
    }
    const n = parseFloat(s);
    return isNaN(n) ? 0 : n;
  }, []);

  const formatHoursForDescription = useCallback((hours: number): string => {
    if (hours <= 0) return "0h";
    let h = Math.floor(hours);
    let m = Math.round((hours - h) * 60);
    if (m === 60) { h += 1; m = 0; }
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  }, []);

  const { data: billingQueryData, isPending: isLoading, refetch: refetchBilling } = useQuery({
    queryKey: ['billing-all'],
    queryFn: async () => {
      const [billedTimeData, fixedFeesData, otherBillingData, mattersData, invoicesData] = await Promise.all([
        FinancialService.getAllBilledTime(),
        FinancialService.getAllAssignedFinancialData(),
        FinancialService.getAllOtherBilling(),
        MatterService.getMatters().catch(() => []),
        InvoiceService.getAllInvoices().catch(() => [])
      ]);

      const billedTimeList = normalizeToArray(billedTimeData);
      const fixedFeesList = normalizeToArray(fixedFeesData);
      const otherBillingList = normalizeToArray(otherBillingData);
      const mattersList = normalizeToArray(mattersData);
      const invoiceNumberMap = buildInvoiceNumberMap(normalizeToArray(invoicesData));

      const matterLookup: Record<string, { mattername: string; status: string }> = {};
      mattersList.forEach((m: any) => {
        if (m.matterid) {
          matterLookup[m.matterid] = {
            mattername: m.mattername || m.matterid,
            status: m.status || "",
          };
        }
      });

      let invoicedItemCount = 0;
      invoicedItemCount += billedTimeList.filter((item: any) => item.invoiceid).length;
      fixedFeesList.forEach((matter: any) => {
        invoicedItemCount += (matter.fixed_fees || []).filter((fee: any) => fee.invoiceid).length;
      });
      invoicedItemCount += otherBillingList.filter((item: any) => item.invoiceid).length;

      const extractCostCode = (notes: string): string => {
        const match = notes.match(/^(\d{3})/);
        return match ? match[1] : "";
      };

      const formattedBilledTimeData: BillingItem[] = billedTimeList.map((item: any, idx: number) => {
        const dateValue = item.date_recorded ?? item.date;
        const notes = (item.notes || "").trim();
        const costCode = extractCostCode(notes);
        // Field Services (900) bills at the employee's field rate, not the standard billed rate.
        const isFieldServices = costCode === "900";
        const rate = isFieldServices ? (Number(item.feildrate) || 0) : (Number(item.billedrate) || 0);
        const costRate = Number(item.costofworkrate) || 0;
        let amount = Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0;
        let hours = parseTimeRecordedToHours(item.time_recorded);
        if (hours <= 0 && amount > 0 && rate > 0) hours = amount / rate;
        if (amount <= 0 && hours > 0 && rate > 0) amount = hours * rate;
        const duration = formatHoursForDescription(hours);
        const invoiceTag = item.invoiceid ? "Invoiced" : "Uninvoiced";
        const description = notes
          ? `${notes} · $${rate}/hr · ${duration} · ${invoiceTag}`
          : `$${rate}/hr · ${duration} · ${invoiceTag}`;
        const matterId = item.matterid || "";
        const matterInfo = matterLookup[matterId];
        return {
          type: "Billable Time",
          id: `billable-${item.bt_id ?? item.id ?? idx}`,
          rawId: String(item.bt_id ?? item.id ?? ""),
          bt_id: item.bt_id != null ? String(item.bt_id) : "",
          amount,
          description,
          date: dateValue ? formatLocalDate(dateValue) : "N/A",
          dateObj: dateValue ? parseLocalDate(dateValue) : new Date(),
          employee: item.employee_name || "Unknown",
          employeeId: item.employeeid || "",
          notes: item.notes || "",
          projectNumber: matterId,
          projectStatus: matterInfo?.status || "",
          costCode,
          hoursQuantity: parseFloat(hours.toFixed(2)),
          costRate,
          billRate: rate,
          invoiceNumber: item.invoiceid ? String(resolveInvoiceNumber(item.invoiceid, invoiceNumberMap)) : "",
          isBillable: item.is_billable !== false, // A3: non-billable time is excluded from the Uninvoiced card count
          rawDateValue: dateValue ? String(dateValue).slice(0, 10) : "",
        };
      });

      const formattedFixedFeesData: BillingItem[] = fixedFeesList.flatMap((matter: any, matterIndex: number) => {
        const matterId = matter.matter_id || matter.matterid || "";
        const matterInfo = matterLookup[matterId];
        return (matter.fixed_fees || []).map((fee: any, feeIndex: number) => ({
          type: "Fixed Fees",
          id: `fixedfee-${matterIndex}-${feeIndex}-${fee.fee_code ?? fee.fee_id ?? feeIndex}`,
          rawId: fee.assignedid != null ? String(fee.assignedid) : "",
          amount: Number.isFinite(parseFloat(fee.fees)) ? parseFloat(fee.fees) : 0,
          description: `${fee.fee_code || ''} - ${fee.narrative || ''}`,
          date: fee.date_added || "N/A",
          dateObj: fee.date_added ? parseLocalDate(fee.date_added) : new Date(),
          employee: "N/A",
          notes: fee.narrative || "",
          projectNumber: matterId,
          projectStatus: matterInfo?.status || "",
          costCode: fee.fee_code || "",
          hoursQuantity: 0,
          costRate: 0,
          billRate: 0,
          invoiceNumber: fee.invoiceid ? String(resolveInvoiceNumber(fee.invoiceid, invoiceNumberMap)) : "",
        }));
      });

      const formattedOtherData: BillingItem[] = otherBillingList.map((item: any, idx: number) => {
        const matterId = item.matterid || "";
        const matterInfo = matterLookup[matterId];
        return {
          type: "Other",
          id: `other-${item.ob_id ?? idx}`,
          rawId: String(item.ob_id ?? ""),
          amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
          description: item.description || "",
          date: item.date ? formatLocalDate(item.date) : "N/A",
          dateObj: item.date ? parseLocalDate(item.date) : new Date(),
          employee: item.employee_name || "Unknown",
          employeeId: item.employeeid || "",
          notes: "",
          projectNumber: matterId,
          projectStatus: matterInfo?.status || "",
          costCode: "",
          hoursQuantity: 0,
          costRate: 0,
          billRate: 0,
          invoiceNumber: item.invoiceid ? String(resolveInvoiceNumber(item.invoiceid, invoiceNumberMap)) : "",
          rawDateValue: item.date ? String(item.date).slice(0, 10) : "",
        };
      });

      const combinedData: BillingItem[] = [
        ...formattedBilledTimeData,
        ...formattedFixedFeesData,
        ...formattedOtherData
      ].sort((a, b) => b.dateObj.getTime() - a.dateObj.getTime());

      return { items: combinedData, invoicedCount: invoicedItemCount };
    },
    staleTime: 2 * 60 * 1000,
  });

  const allBillingData = billingQueryData?.items ?? [];
  const invoicedCount = billingQueryData?.invoicedCount ?? 0;

  const fetchAllBillingData = useCallback(() => { refetchBilling(); }, [refetchBilling]);

  // Filter billing data based on filters
  const filteredBilling: BillingItem[] = useMemo(() => {
    let filtered = [...allBillingData];

    if (typeFilter !== "All types") {
      filtered = filtered.filter(item => item.type === typeFilter);
    }

    const now = calgaryCalendarDate();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    if (dateFilter === "Last 7 Days") {
      const cutoff = todayStart - 7 * 24 * 60 * 60 * 1000;
      filtered = filtered.filter(item => item.dateObj.getTime() >= cutoff);
    } else if (dateFilter === "Last 90 Days") {
      const cutoff = todayStart - 90 * 24 * 60 * 60 * 1000;
      filtered = filtered.filter(item => item.dateObj.getTime() >= cutoff);
    } else if (dateFilter === "This Year") {
      const currentYear = now.getFullYear();
      filtered = filtered.filter(item => item.dateObj.getFullYear() === currentYear);
    }

    if (invoiceFilter === "invoiced") {
      filtered = filtered.filter(item => !!item.invoiceNumber);
    } else if (invoiceFilter === "uninvoiced") {
      filtered = filtered.filter(item => !item.invoiceNumber);
    }

    if (searchQuery) {
      filtered = filtered.filter(item =>
        item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.employee.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  }, [allBillingData, typeFilter, dateFilter, invoiceFilter, searchQuery]);

  // Summary metrics derived from the FILTERED view so the cards match the table.
  const metrics: BillingMetrics = useMemo(() => {
    const totalBilling = filteredBilling.reduce((sum, item) => sum + item.amount, 0);

    const typeGroups: { [key: string]: number } = filteredBilling.reduce((acc, item) => {
      acc[item.type] = (acc[item.type] || 0) + item.amount;
      return acc;
    }, {} as { [key: string]: number });
    const topCategory = Object.entries(typeGroups).sort((a, b) => b[1] - a[1])[0]?.[0] || "Billable Time";

    const uninvoiced = filteredBilling.filter(item => !item.invoiceNumber && item.isBillable !== false); // A3: exclude non-billable from the Uninvoiced queue count
    const uninvoicedAmount = uninvoiced.reduce((sum, item) => sum + item.amount, 0);
    const uninvoicedCount = uninvoiced.length;
    const invoicedAmount = totalBilling - uninvoicedAmount;

    return { uninvoicedAmount, uninvoicedCount, totalBilling, topCategory, invoicedAmount };
  }, [filteredBilling]);

  const totalFiltered = filteredBilling.length;
  const pageLimit = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const paginatedBilling = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredBilling.slice(start, start + pageSize);
  }, [filteredBilling, page, pageSize]);

  const handleExport = (): void => {
    setShowExportModal(true);
  };

  const handleAddTransaction = (): void => {
    setShowAddModal(true);
  };

  const handleModalSuccess = (): void => {
    fetchAllBillingData();
  };

  const openEditModal = useCallback((item: BillingItem) => {
    setEditingItem(item);
    setEditFormData({
      amount: item.amount.toFixed(2),
      description: item.type === "Billable Time" ? (item.notes || "") : item.description,
      date: item.rawDateValue || "",
      hours: item.hoursQuantity > 0 ? String(item.hoursQuantity) : "",
    });
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingItem) return;
    setIsSavingEdit(true);
    try {
      const { type, rawId } = editingItem;
      if (type === "Billable Time") {
        const hours = parseFloat(editFormData.hours) || 0;
        const h = Math.floor(hours);
        const m = Math.round((hours - h) * 60);
        const timeRecorded = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
        const rate = editingItem.billRate || 0;
        const amount = hours * rate;
        await FinancialService.updateBilledTime(rawId, {
          time_recorded: timeRecorded,
          date_recorded: editFormData.date,
          notes: editFormData.description,
          matterid: editingItem.projectNumber,
          employeeid: editingItem.employeeId || "",
          amount: String(Number(amount.toFixed(2))),
          tax_percent: "0",
        });
      } else if (type === "Other") {
        await FinancialService.updateOtherBilling(rawId, {
          employeeid: editingItem.employeeId || "",
          description: editFormData.description,
          amount: editFormData.amount,
          tax_percent: "0",
          matterid: editingItem.projectNumber,
          date: editFormData.date || null,
        });
      }
      setEditingItem(null);
      fetchAllBillingData();
    } catch (error) {
      console.error("Error saving edit:", error);
    } finally {
      setIsSavingEdit(false);
    }
  }, [editingItem, editFormData, fetchAllBillingData]);

  const handleDelete = useCallback(async () => {
    if (!deleteItem) return;
    const { type, rawId } = deleteItem;
    if (!rawId) {
      console.error("Cannot delete: missing record ID for", type);
      return;
    }
    setIsDeleting(true);
    try {
      if (type === "Billable Time") {
        await FinancialService.deleteBilledTime(rawId);
      } else if (type === "Fixed Fees") {
        await FinancialService.deleteAssignedFixedFee(rawId);
      } else if (type === "Other") {
        await FinancialService.deleteOtherBilling(rawId);
      }
      setDeleteItem(null);
      fetchAllBillingData();
    } catch (error) {
      console.error("Error deleting item:", error);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteItem, fetchAllBillingData]);

  // Define table columns
  const columns: Column<BillingItem>[] = [
    {
      header: "TYPE",
      render: (row: BillingItem) => (
        <span className="general-billing-type">{row.type}</span>
      ),
    },
    {
      header: "AMOUNT",
      render: (row: BillingItem) => (
        <span className="general-billing-amount">${row.amount.toFixed(2)}</span>
      ),
    },
    {
      header: "DESCRIPTION",
      accessor: "description",
      render: (row: BillingItem) => (
        <span className="general-billing-description">{row.description}</span>
      ),
    },
    {
      header: "DATE",
      accessor: "date",
      render: (row: BillingItem) => (
        <span className="general-billing-date">{row.date}</span>
      ),
    },
    {
      header: "EMPLOYEE",
      accessor: "employee",
      render: (row: BillingItem) => (
        <span className="general-billing-employee">{row.employee}</span>
      ),
    },
    ...(!isTimesheetOnlyUser ? [{
      header: "ACTIONS",
      render: (row: BillingItem) => {
        const isInvoiced = !!row.invoiceNumber;
        return (
          <div className="general-billing-actions-cell" onClick={(e) => e.stopPropagation()}>
            {(row.type === "Billable Time" || row.type === "Other") && (
              <button
                className="general-billing-action-btn general-billing-edit-btn"
                onClick={() => openEditModal(row)}
                title="Edit"
                disabled={isInvoiced}
              >
                <FontAwesomeIcon icon={faPenToSquare} />
              </button>
            )}
            <button
              className="general-billing-action-btn general-billing-delete-btn"
              onClick={() => setDeleteItem(row)}
              title="Delete"
              disabled={isInvoiced}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        );
      },
    }] as Column<BillingItem>[] : []),
  ];

  if (!permissionsResolved) {
    return (
      <div className="general-billing-page-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <span>Loading...</span>
      </div>
    );
  }

  return (
    <div className="general-billing-page-container">
      <div className="general-billing-header">
        <div>
          <h2>{isTimesheetOnlyUser ? "Timesheets" : "Billing"}</h2>
          <p className="general-header-sub">
            {isTimesheetOnlyUser ? "Track and manage your time entries." : "Track all financials here."}
          </p>
        </div>
        {!isTimesheetOnlyUser ? (
          <div className="general-billing-actions">
            <button className="general-billing-ghost-btn" onClick={() => setShowInvoicedPanel(true)}>
              <FontAwesomeIcon icon={faFileInvoice} />
              <span>Already Invoiced</span>
              {invoicedCount > 0 && (
                <span className="general-billing-badge">{invoicedCount}</span>
              )}
            </button>
            <button className="general-billing-ghost-btn" onClick={() => setShowChartsModal(true)}>
              <FontAwesomeIcon icon={faChartLine} />
              <span>Charts</span>
            </button>
            <button className="general-billing-ghost-btn" onClick={handleExport}>
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
              <span>Export</span>
            </button>
            <button className="general-billing-primary-btn" onClick={handleAddTransaction}>
              <FontAwesomeIcon icon={faPlus} />
              <span>Add Billable Entry</span>
            </button>
          </div>
        ) : (
          <div className="general-billing-actions">
            <button className="general-billing-ghost-btn" onClick={handleExport}>
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
              <span>Export</span>
            </button>
          </div>
        )}
      </div>

      {/* Summary Cards - List view only, and hidden for timesheet-only users */}
      {!isTimesheetOnlyUser && viewMode === "list" && <div className="general-billing-cards">
        <div className="general-billing-card general-billing-card-green">
          <div className="general-billing-card-icon general-green-icon">
            <FontAwesomeIcon icon={faArrowUp} />
          </div>
          <div className="general-billing-card-content">
            <div className="general-billing-card-label">Uninvoiced</div>
            <div className="general-billing-card-value">${metrics.uninvoicedAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div className="general-billing-card-sub">{metrics.uninvoicedCount} item{metrics.uninvoicedCount === 1 ? '' : 's'}</div>
          </div>
        </div>

        <div className="general-billing-card general-billing-card-red">
          <div className="general-billing-card-icon general-red-icon">
            <FontAwesomeIcon icon={faArrowUp} />
          </div>
          <div className="general-billing-card-content">
            <div className="general-billing-card-label">Total Billing</div>
            <div className="general-billing-card-value">${metrics.totalBilling.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        </div>

        <div className="general-billing-card general-billing-card-blue">
          <div className="general-billing-card-icon general-blue-icon">
            <FontAwesomeIcon icon={faArrowUp} />
          </div>
          <div className="general-billing-card-content">
            <div className="general-billing-card-label">Top Category</div>
            <div className="general-billing-card-value">{metrics.topCategory}</div>
          </div>
        </div>

        <div className="general-billing-card general-billing-card-yellow">
          <div className="general-billing-card-icon general-yellow-icon">
            <FontAwesomeIcon icon={faClock} />
          </div>
          <div className="general-billing-card-content">
            <div className="general-billing-card-label">Invoiced</div>
            <div className="general-billing-card-value">${metrics.invoicedAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        </div>
      </div>}

      {/* View Toggle and Filters */}
      <div className="general-billing-filters">
        {!isTimesheetOnlyUser && (
          <div className="general-billing-view-toggle">
            <button
              type="button"
              className={`general-billing-toggle-btn ${viewMode === "list" ? "active" : ""}`}
              onClick={() => setViewMode("list")}
            >
              <FontAwesomeIcon icon={faList} />
              <span>List</span>
            </button>
            <button
              type="button"
              className={`general-billing-toggle-btn ${viewMode === "timesheet" ? "active" : ""}`}
              onClick={() => setViewMode("timesheet")}
            >
              <FontAwesomeIcon icon={faCalendarWeek} />
              <span>Timesheet</span>
            </button>
          </div>
        )}
        {!isTimesheetOnlyUser && viewMode === "list" && (
          <>
            <div className="general-billing-search">
              <input
                type="text"
                placeholder="Search Billing..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                className="general-billing-search-input"
              />
            </div>
            <div className="general-billing-filter-dropdowns">
              <select
                value={typeFilter}
                onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
                className="general-billing-filter-select"
              >
                <option>All types</option>
                <option>Billable Time</option>
                <option>Fixed Fees</option>
                <option>Other</option>
              </select>
              <select
                value={dateFilter}
                onChange={(e) => { setDateFilter(e.target.value); setPage(1); }}
                className="general-billing-filter-select"
              >
                <option>All time</option>
                <option>Last 7 Days</option>
                <option>Last 90 Days</option>
                <option>This Year</option>
              </select>
              <select
                value={invoiceFilter}
                onChange={(e) => { setInvoiceFilter(e.target.value as "all" | "invoiced" | "uninvoiced"); setPage(1); }}
                className="general-billing-filter-select"
              >
                <option value="all">All statuses</option>
                <option value="invoiced">Invoiced</option>
                <option value="uninvoiced">Uninvoiced</option>
              </select>
            </div>
          </>
        )}
      </div>

      {/* Content based on view mode - timesheet-only users always see timesheet */}
      {isTimesheetOnlyUser || viewMode === "timesheet" ? (
        <GeneralTimesheetView weeklyBudgetCapHours={40} />
      ) : isLoading ? (
        <p style={{ padding: '20px' }}>Loading billing data...</p>
      ) : (
        <Table
          data={paginatedBilling}
          rowKey="id"
          columns={columns}
          emptyMessage="No billing data available"
          pagination={{
            currPage: page,
            onPageChange: (p: number) => setPage(p),
            pageLimit,
            pageSize,
            onPageSizeChange: (s: number) => { setPageSize(s); setPage(1); },
            totalItems: totalFiltered,
            itemLabel: "entries",
          }}
        />
      )}

      {/* Add Billable Entry Modal */}
      <LogBillableEntryModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleModalSuccess}
      />

      <BillingExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        data={filteredBilling}
        ownTimeCardOnly={isTimesheetOnlyUser}
        currentEmployeeId={activeEmployeeId ?? currentEmployee?.employeeid ?? null}
      />

      {/* Already Invoiced Panel */}
      {showInvoicedPanel && (
        <AlreadyInvoicedPanel
          onClose={() => setShowInvoicedPanel(false)}
        />
      )}

      {/* Billing Analytics Charts */}
      <BillingChartsModal
        isOpen={showChartsModal}
        onClose={() => setShowChartsModal(false)}
        data={filteredBilling}
      />

      {/* Edit Modal */}
      {editingItem && (
        <div className="general-billing-modal-overlay" onClick={() => setEditingItem(null)}>
          <div className="general-billing-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit {editingItem.type}</h3>
            <div className="general-billing-modal-form">
              {editingItem.type === "Billable Time" && (
                <>
                  <label>
                    <span>Notes</span>
                    <input
                      type="text"
                      value={editFormData.description}
                      onChange={(e) => setEditFormData(p => ({ ...p, description: e.target.value }))}
                    />
                  </label>
                  <label>
                    <span>Hours</span>
                    <input
                      type="number"
                      step="0.25"
                      min="0"
                      value={editFormData.hours}
                      onChange={(e) => setEditFormData(p => ({ ...p, hours: e.target.value }))}
                    />
                  </label>
                  <label>
                    <span>Date</span>
                    <input
                      type="date"
                      value={editFormData.date}
                      onChange={(e) => setEditFormData(p => ({ ...p, date: e.target.value }))}
                    />
                  </label>
                  <div className="general-billing-modal-readonly">
                    <span>Rate: ${editingItem.billRate}/hr</span>
                    <span>Amount: ${((parseFloat(editFormData.hours) || 0) * editingItem.billRate).toFixed(2)}</span>
                  </div>
                </>
              )}
              {editingItem.type === "Other" && (
                <>
                  <label>
                    <span>Description</span>
                    <input
                      type="text"
                      value={editFormData.description}
                      onChange={(e) => setEditFormData(p => ({ ...p, description: e.target.value }))}
                    />
                  </label>
                  <label>
                    <span>Amount</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editFormData.amount}
                      onChange={(e) => setEditFormData(p => ({ ...p, amount: e.target.value }))}
                    />
                  </label>
                  <label>
                    <span>Date</span>
                    <input
                      type="date"
                      value={editFormData.date}
                      onChange={(e) => setEditFormData(p => ({ ...p, date: e.target.value }))}
                    />
                  </label>
                </>
              )}
            </div>
            <div className="general-billing-modal-actions">
              <button className="general-billing-ghost-btn" onClick={() => setEditingItem(null)} disabled={isSavingEdit}>
                Cancel
              </button>
              <button className="general-billing-primary-btn" onClick={handleSaveEdit} disabled={isSavingEdit}>
                {isSavingEdit ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteItem && (
        <div className="general-billing-modal-overlay" onClick={() => setDeleteItem(null)}>
          <div className="general-billing-modal general-billing-modal-sm" onClick={(e) => e.stopPropagation()}>
            <h3>Delete {deleteItem.type}</h3>
            <p>Are you sure you want to delete this entry? This action cannot be undone.</p>
            <div className="general-billing-modal-detail">
              <span>{deleteItem.description}</span>
              <span>${deleteItem.amount.toFixed(2)}</span>
            </div>
            <div className="general-billing-modal-actions">
              <button className="general-billing-ghost-btn" onClick={() => setDeleteItem(null)} disabled={isDeleting}>
                Cancel
              </button>
              <button className="general-billing-delete-confirm-btn" onClick={handleDelete} disabled={isDeleting}>
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
